<template>
  <div class="search-education">
    <header class="search-education__header">
      <af-h3>Välj lägsta utbildningsnivå</af-h3>
    </header>

    <div class="search-education__wrapper">
      <div class="search-education__box-wrap">
        <div class="search-education__box">
          <h4>Lägsta Utbildningsnivå</h4>

          <search-dropdown
            :options="educationLevels"
            :value="getEducationPath"
            inlinePlaceholder="Välj utbildningsnivå"
            name="educationRanking"
            @changed="setEducationRanking($event)">
          </search-dropdown>

          <div v-if="getRanking >= 50" class="search-education__box-subcategories">
            <h4>Utbildningsinriktning <span>(valfri)</span></h4>

            <search-dropdown
              :options="getSortedEducationSubjects"
              :value="getSubjectPath"
              inlinePlaceholder="Välj utbildningsinriktning"
              name="educationSubject"
              @changed="setEducationSubject($event)">
            </search-dropdown>

            <h4>Antal år <span>(valfri)</span></h4>

            <search-dropdown
              :options="years"
              :value="getYears"
              inlinePlaceholder="Välj minsta antal år"
              name="educationYears"
              @changed="setEducationYears($event)">
            </search-dropdown>
          </div>

          <div v-if="getRanking >= 40">
            <h4>Utbildningsdokument <span>(valfri)</span></h4>

            <div class="search-education__box-frame">
              <af-checkbox
                :checked="getHasDegree"
                name="hasDegree"
                @changed="toggleInclude($event)">
                Krav på examen
              </af-checkbox>
            </div>

            <div class="search-education__box-frame">
              <af-checkbox
                :checked="getHasDocuments"
                name="hasDocuments"
                @changed="toggleInclude($event)">
                Krav på dokument
              </af-checkbox>
            </div>

            <div class="search-education__box-frame">
              <af-checkbox
                :checked="getHasDocumentsEnglish"
                name="hasDocumentsEnglish"
                @changed="toggleInclude($event)">
                Krav på dokument på engelska
              </af-checkbox>
            </div>
          </div>

          <div v-if="getRanking" class="search-education__box-button">
            <af-button
              buttonStyle="primary"
              lowercase
              @clicked="updateEducation">
              Lägg till
            </af-button>
          </div>
        </div>
      </div>

      <af-info-box class="search-education__info-box" :links="infoLinks">
        <template slot="title">Bedömning av utländska utbildningar</template>
        <p slot="content">Myndigheten Universitets- och högskolerådet (UHR) bedömer utländska utbildningar och ger utlåtanden om vad utländska utbildningar motsvarar i det svenska utbildningssystemet.</p>
        <p slot="content">UHR bedömer yrkesutbildningar och akademiska utbildningar på gymnasial och eftergymnasial nivå.</p>
        <p slot="content">För yrken som är reglerade i Sverige (till exempel yrken inom sjukvård och utbildning) är det den aktuella myndigheten som bedömer utbildningen och ger en legitimation för yrket.</p>
        <p slot="content">Alla personer som är nya i Sverige kan ansöka om att få sin utbildning bedömd och om att få legitimation för reglerade yrken.</p>
        <p slot="content">Personer med uppehållstillstånd som är inskrivna på Arbetsförmedlingen kan få hjälp att validera sin yrkeskunskap genom Arbetsförmedlingen om de saknar intyg eller utbildningsdokument.</p>
      </af-info-box>
    </div>
  </div>
</template>

<script>
import * as types from '@/store/actionTypes'
import gql from 'graphql-tag'
import AfCheckbox from '@/components/AfCheckbox'
import AfH3 from '@/components/AfH3'
import SearchDropdown from './SearchDropdown'
import Icon from '@/components/Icon'
import AfInfoBox from '@/components/AfInfoBox'
import AfButton from '@/components/Button/Button'

export default {
  name: 'SearchEducation',

  components: {
    AfCheckbox,
    AfH3,
    SearchDropdown,
    Icon,
    AfInfoBox,
    AfButton
  },

  data() {
    return {
      educationQuery: {
        name: '',
        path: '',
        ranking: 0,
        hasDegree: false,
        hasDocuments: false,
        hasDocumentsEnglish: false,
        years: [],
        subject: {
          name: '',
          path: ''
        }
      },
      infoLinks: [
        {
          text: 'Läs mer om hur UHR bedömer utländska utbildningar',
          href: 'https://www.uhr.se/bedomning-av-utlandsk-utbildning/',
          external: true
        },
        {
          text: 'Läs mer om reglerade yrken',
          href:
            'https://www.uhr.se/bedomning-av-utlandsk-utbildning/information-innan-ansokan/Arbeta-i-Sverige/Reglerade-yrken/',
          external: true
        }
      ],
      educationLevels: [],
      educationSubjects: [],
      years: [
        {
          name: 'Oavsett antal år',
          path: 0
        },
        {
          name: 'Minst ett år',
          path: 1
        },
        {
          name: 'Minst två år',
          path: 2
        },
        {
          name: 'Minst tre år',
          path: 3
        },
        {
          name: 'Minst fyra år',
          path: 4
        },
        {
          name: 'Minst fem år',
          path: 5
        }
      ]
    }
  },

  computed: {
    getRanking() {
      return this.educationQuery.ranking
    },
    getEducationPath() {
      return this.educationQuery.path
    },
    getSubjectPath() {
      return this.educationQuery.subject.path
    },
    getYears() {
      if (this.educationQuery.years.length) {
        return this.educationQuery.years[0].years
      }
      return null
    },
    getHasDegree() {
      return this.educationQuery.hasDegree
    },
    getHasDocuments() {
      return this.educationQuery.hasDocuments
    },
    getHasDocumentsEnglish() {
      return this.educationQuery.hasDocumentsEnglish
    },
    getSortedEducationSubjects() {
      if (!this.educationSubjects.length) {
        return []
      }

      return this.educationSubjects.sort((a, b) => {
        // Puts inline placeholder at the top
        // and sort by name
        if (a.path === 0 || a.name < b.name) {
          return -1
        }

        if (a.name > b.name) {
          return 1
        }

        return 0
      })
    }
  },

  apollo: {
    educationLevelsData: {
      query: gql`
        query EducationLevels($language: LanguageCode!) {
          educationLevels(language: $language) {
            name
            path
            ranking
          }
          educationSubjects(language: $language) {
            name
            path
          }
        }
      `,
      variables() {
        return {
          language: 'sv'
        }
      },
      update({ educationLevels, educationSubjects }) {
        this.handleApolloUpdate({ educationLevels, educationSubjects })
      }
    }
  },

  methods: {
    handleApolloUpdate({ educationLevels, educationSubjects }) {
      // Slice makes a copy, this is needed because the
      // dropdown mutates the data which should be fixed
      this.educationLevels = educationLevels.slice(0)
      this.educationSubjects = educationSubjects.slice(0)
    },

    toggleInclude(event) {
      if (this.educationQuery[event] === undefined) {
        return
      }

      this.educationQuery[event] = !this.educationQuery[event]
    },

    setEducationRanking(event) {
      this.educationQuery.ranking = event.ranking
      this.educationQuery.path = event.path
      this.educationQuery.name = event.name
    },

    setEducationSubject(event) {
      this.educationQuery.subject = { name: event.name, path: event.path }
    },

    setEducationYears(event) {
      this.educationQuery.years = [{ modifier: 'gt', years: event.path }]
    },

    updateEducation() {
      this.$store.commit(types.ADD_EDUCATION_TO_QUERY, {
        payload: { ...this.educationQuery },
        type: 'educations'
      })

      this.educationQuery = {
        name: '',
        path: '',
        ranking: 0,
        hasDegree: false,
        hasDocuments: false,
        hasDocumentsEnglish: false,
        years: [],
        subject: {
          name: '',
          path: ''
        }
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
@import '../../variables/custom-media.css';

.search-education {
  background-color: var(--color-white);
  padding: var(--spacing) var(--spacing-medium);
}

.search-education__header {
  margin-bottom: var(--spacing);
}

.search-education__wrapper {
  display: block;

  @media (--bp-desktop) {
    display: grid;
    display: -ms-grid;
    grid-template-columns: 1fr 2fr;
    grid-column-gap: var(--spacing-large);
    -ms-grid-columns: 1fr 2fr;
    -ms-grid-rows: 1fr;
  }
}

.search-education__box-wrap {
  @media (--ie11) and (--bp-desktop) {
    -ms-grid-column: 1;
    -ms-grid-row: 1;
    margin-right: var(--spacing-large);
  }
}

.search-education__box {
  background-color: var(--color-porcelain);
  margin-bottom: var(--spacing-medium);
  padding: var(--spacing-half);

  @media (--bp-desktop) {
    margin-bottom: 0;
  }
}

.search-education__box-frame {
  background: var(--color-white);
  border: 1px solid var(--color-iron);
  border-radius: 5px;
  padding: var(--spacing-half);
  margin: var(--spacing-third) 0;
}

.search-education__box-button {
  display: flex;
  justify-content: center;
  margin: var(--spacing-medium) 0;
}

.search-education__box-subcategories {
  margin-top: var(--spacing-large);
}

.search-education__info {
  background-color: var(--color-porcelain);
  padding: var(--spacing-half);
  border-radius: 4px;
  font-size: 16px;
  line-height: 1.5;
  padding: var(--spacing);
}

.search-education__info-icon {
  color: var(--color-allports);
  font-size: 20px;
  margin-right: var(--spacing-third);
}

.search-education__info-link {
  color: #13769d;
  display: block;
  font-weight: 600;
  text-decoration: none;

  & .fa-angle-right {
    margin-top: 5px;
  }
}

h3 {
  font-size: 20px;
  font-weight: 600;
}

h4 {
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;

  & span {
    text-transform: none;
    color: var(--color-rolling-stone);
  }
}

.search-education__info-box {
  @media (--ie11) and (--bp-desktop) {
    -ms-grid-column: 2;
    -ms-grid-row: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Search/SearchEducation.vue